import { Autocomplete, TextField } from "@mui/material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useRoomInfoQuery, useRoomsListQuery } from "@/api/hooks/table-cache-hooks";
import { useMuiTableToolbarAlertBannerProps } from "@/hooks/use-mui-table-toolbar-alert-banner-props";
import { useTableSettings } from "@/hooks/use-table-settings";
import { useTableCacheTableColumns } from "@/module/service-configs-module/table-cache-module/table-cache-table-columns";
import { RoomTablesCacheTableDto } from "@/shared/api";

const TableCacheTable = ({
  setRowsAmount,
  selectedRoom,
  setSelectedRoom,
  tablesQuery,
  roomsQuery,
}: {
  setRowsAmount: (amount: number) => void;
  selectedRoom: string | null;
  setSelectedRoom: (value: string | null) => void;
  tablesQuery: ReturnType<typeof useRoomInfoQuery>;
  roomsQuery: ReturnType<typeof useRoomsListQuery>;
}) => {
  const { t } = useTranslation();
  const columns = useTableCacheTableColumns();
  const { state, defaultProps } = useTableSettings<RoomTablesCacheTableDto>("playersTable");

  const { data: roomList, isLoading: isRoomsListLoading } = roomsQuery;

  const roomsListOptions = useMemo(() => {
    if (roomList) {
      return roomList.data.rooms;
    }
    return [];
  }, [roomList]);

  const {
    data: tablesResponse,
    isError: isDataLoadingError,
    isLoading: isDataLoading,
    isRefetching: isDataRefetching,
  } = tablesQuery;

  const muiToolbarAlertBannerProps = useMuiTableToolbarAlertBannerProps(
    isDataLoadingError,
    tablesResponse?.statusText
  );

  useEffect(() => {
    setRowsAmount(tablesResponse?.data.tables.length ?? 0);
  }, [tablesResponse, setRowsAmount]);

  const table = useMaterialReactTable({
    data: tablesResponse?.data.tables ?? [],
    muiToolbarAlertBannerProps,
    columns: columns,
    rowCount: tablesResponse?.data.tables.length ?? 0,
    state: {
      ...state,
      isLoading: isDataLoading,
      showProgressBars: isDataRefetching,
    },
    ...defaultProps,
    renderTopToolbarCustomActions: () => {
      return (
        <div style={{ height: "40px", display: "flex", alignItems: "center" }}>
          <Autocomplete
            onChange={(_event, newValue) => {
              setSelectedRoom(newValue);
            }}
            value={selectedRoom}
            loading={isRoomsListLoading}
            options={roomsListOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                style={{ width: 200 }}
                label={t("selectRoom")}
                variant="outlined"
              />
            )}
          />
        </div>
      );
    },
    selectAllMode: "page",
  });

  return <MaterialReactTable table={table} />;
};

export default TableCacheTable;
